import { Fragment, useCallback, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BlockNoteEditorComponent } from '@/components/atoms/Editors/BlockNoteEditor/BlockNoteEditor';
import { getGuidelinesEditorOverrides } from '@/components/atoms/Editors/BlockNoteEditor/config';
import { Separator } from '@/components/ui/separator';
import useBrains from '@/hooks/useBrains';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import type { ChannelGuidelines, ChannelType } from '@/shared/types/brain';
import type { Dialog } from '@/shared/types/dialog';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { GuidelineEditorHeader } from './GuidelineEditorHeader';

const CHANNELS: { value: ChannelType; labelKey: string }[] = [
  { value: 'voice' as ChannelType, labelKey: 'ai_agents.channel_guidelines.voice' },
  { value: 'email' as ChannelType, labelKey: 'ai_agents.channel_guidelines.email' },
  { value: 'chat' as ChannelType, labelKey: 'ai_agents.channel_guidelines.chat' },
];

export type ChannelGuidelinesEditorHandle = {
  getMarkdown: () => Promise<ChannelGuidelines>;
  /** True when any channel's content differs from its baseline. */
  hasChanges: () => Promise<boolean>;
  /** Re-snapshot baselines from current content (call after a successful save). */
  resetBaseline: () => Promise<void>;
};

type ChannelEditorHandlers = {
  onEditorReady: (editor: { getMarkdown: () => Promise<string> }) => void;
  onChange: () => void;
};

type ChannelGuidelinesEditorProps = {
  /** Falls back to the session id. Pass it to survive the rename sidebar's null blip. */
  brainId?: string;
  onEditorReady?: (handle: ChannelGuidelinesEditorHandle) => void;
  onChange?: () => void;
  onChannelChange?: (channel: ChannelType, markdown: string) => void;
  registerSectionRef?: (channel: ChannelType) => (node: HTMLElement | null) => void;
  enabledTools?: string[];
  dialogs?: Dialog[];
  onNavigateToLocalTools?: () => void;
};

export const ChannelGuidelinesEditor = ({
  brainId: brainIdProp,
  onEditorReady,
  onChange,
  onChannelChange,
  registerSectionRef,
  enabledTools,
  dialogs,
  onNavigateToLocalTools,
}: ChannelGuidelinesEditorProps) => {
  const { t } = useTranslation();
  const sessionBrainId = useSelector(selectBrainId);
  const brainId = brainIdProp ?? sessionBrainId;
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { brain, getStatus } = useBrains(brainId);
  const { contextVariables } = useBrainVariables();

  const editorRefs = useRef<Partial<Record<ChannelType, { getMarkdown: () => Promise<string> }>>>(
    {}
  );
  // Baselines snapshotted as each editor fires ready. `getMarkdown` reads
  // `editor.document` synchronously, so later edits can't leak into them.
  const initialMarkdownRefs = useRef<Partial<Record<ChannelType, Promise<string>>>>({});

  const collectChannelMarkdown = useCallback(
    async (
      resolve: (channel: ChannelType) => Promise<string> | undefined
    ): Promise<ChannelGuidelines> => {
      const markdowns = await Promise.all(CHANNELS.map(({ value }) => resolve(value)));
      const result: ChannelGuidelines = {};
      CHANNELS.forEach(({ value }, idx) => {
        if (markdowns[idx]) {
          result[value] = markdowns[idx];
        }
      });
      return result;
    },
    []
  );

  const getMarkdown = useCallback(
    () => collectChannelMarkdown((channel) => editorRefs.current[channel]?.getMarkdown()),
    [collectChannelMarkdown]
  );

  const hasChanges = useCallback(async () => {
    const [current, initial] = await Promise.all([
      getMarkdown(),
      collectChannelMarkdown((channel) => initialMarkdownRefs.current[channel]),
    ]);
    return CHANNELS.some(({ value }) => {
      const currentValue = (current[value] ?? '').trim();
      const initialValue = (initial[value] ?? '').trim();
      return currentValue !== initialValue;
    });
  }, [getMarkdown, collectChannelMarkdown]);

  const resetBaseline = useCallback(async () => {
    for (const { value } of CHANNELS) {
      const editor = editorRefs.current[value];
      if (editor) {
        initialMarkdownRefs.current[value] = editor.getMarkdown();
      }
    }
    // Settle the snapshots so callers can await a consistent baseline.
    await Promise.all(Object.values(initialMarkdownRefs.current));
  }, []);

  const handle = useMemo<ChannelGuidelinesEditorHandle>(
    () => ({ getMarkdown, hasChanges, resetBaseline }),
    [getMarkdown, hasChanges, resetBaseline]
  );

  // A Set, not a counter: the guard below unmounts and re-registers all three.
  const readyChannels = useRef(new Set<ChannelType>());

  // Drop what the editors registered as the guard below unmounts them. In render,
  // not an effect: an effect runs after their remount and wipes it again (#4080).
  const areEditorsMounted = getStatus === 'success';
  const wereEditorsMounted = useRef(false);
  if (wereEditorsMounted.current && !areEditorsMounted) {
    readyChannels.current.clear();
    editorRefs.current = {};
    initialMarkdownRefs.current = {};
  }
  wereEditorsMounted.current = areEditorsMounted;

  const channelHandlers = useMemo(() => {
    const handlersFor = (channel: ChannelType): ChannelEditorHandlers => ({
      onEditorReady: (editor: { getMarkdown: () => Promise<string> }) => {
        editorRefs.current[channel] = editor;
        initialMarkdownRefs.current[channel] = editor.getMarkdown().catch((error) => {
          console.error('Failed to capture channel guidelines baseline:', error);
          return '';
        });
        readyChannels.current.add(channel);
        if (readyChannels.current.size === CHANNELS.length) {
          onEditorReady?.(handle);
        }
      },
      onChange: () => {
        onChange?.();
        if (onChannelChange) {
          editorRefs.current[channel]?.getMarkdown().then((markdown) => {
            onChannelChange(channel, markdown);
          });
        }
      },
    });

    return CHANNELS.reduce(
      (acc, { value: channel }) => {
        acc[channel] = handlersFor(channel);
        return acc;
      },
      {} as Record<ChannelType, ChannelEditorHandlers>
    );
  }, [onEditorReady, onChange, onChannelChange, handle]);

  if (!areEditorsMounted) {
    return null;
  }

  const channelGuidelines = brain?.guidelines?.channel_guidelines;

  return (
    <div>
      {CHANNELS.map(({ value, labelKey }, idx) => (
        <Fragment key={value}>
          <section
            id={`guideline-${value}`}
            ref={registerSectionRef?.(value)}
            className="scroll-mt-28"
          >
            <GuidelineEditorHeader title={t(labelKey)} />
            <BlockNoteEditorComponent
              onSave={() => {}}
              initialMarkdown={channelGuidelines?.[value] ?? ''}
              dictionary={getGuidelinesEditorOverrides().dictionary}
              editable={canWrite}
              {...channelHandlers[value]}
              contextVariableOptions={contextVariables}
              enabledTools={enabledTools}
              dialogs={dialogs}
              onNavigateToLocalTools={onNavigateToLocalTools}
            />
          </section>
          {idx < CHANNELS.length - 1 && <Separator decorative className="my-6 text-border" />}
        </Fragment>
      ))}
    </div>
  );
};
